import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { StatusBarStyleValue } from '@bp-core/src/lib/models/IInvokeCsharpActionData';
import { WVCommunicationService } from '@bp-core/src/lib/services/wvcommunication.service';
import { filter } from 'rxjs';

/**
 * Mantiene el color de los iconos de la status bar del sistema (hora, señal, bateria)
 * legible sobre lo que el portal dibuja debajo de ella.
 *
 * Dentro de la app movil el portal se dibuja de borde a borde, asi que la status bar
 * quedo por encima del contenido y su contraste ya no lo decide la app: lo decide el
 * fondo de la vista. Como ese fondo cambia por pantalla (gris del canvas, blanco del
 * header de pasos, negro de la camara), fijar el color desde el codigo nativo obliga a
 * declararlo vista por vista y a acordarse de hacerlo en cada pantalla nueva.
 *
 * En vez de eso, este servicio MIDE el color real que hay bajo la franja superior y
 * deriva el estilo de su luminancia. Cualquier vista futura queda cubierta sin tocar
 * nada.
 *
 * Fuera de la app movil no hace nada: si `--bp-safe-top` es 0 no hay franja que
 * evaluar, y `WVCommunicationService` de por si solo emite cuando corre embebido.
 */
@Injectable({ providedIn: 'root' })
export class BpNativeStatusBarService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly wvCommunicationService = inject(WVCommunicationService);

  /** Ultimo estilo emitido, para no repetir el mismo mensaje en cada scroll. */
  private lastSent: StatusBarStyleValue | null = null;
  /** Evita encolar varias mediciones dentro del mismo frame. */
  private scheduled = false;
  private started = false;

  /**
   * Overrides explicitos apilados por `BpStatusBarStyleDirective`. Mientras haya alguno,
   * manda el de mas arriba y la deteccion automatica no lo pisa: un overlay sabe mejor
   * que la medicion cual es su fondo (por ejemplo mientras hace su animacion de entrada).
   */
  private readonly overrides: StatusBarStyleValue[] = [];

  /**
   * Arranca la sincronizacion. Se llama una unica vez desde el AppComponent, de modo que
   * cubre los tres layouts del portal sin que ninguno tenga que saber de esto.
   */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.schedule());

    // El header cambia de fondo al hacer scroll (transparente → blanco translucido con
    // blur), asi que el contraste puede cambiar sin que haya navegacion.
    // Fuera de la zona de Angular: es un evento de alta frecuencia y no toca la vista.
    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('scroll', () => this.schedule(), {
        passive: true,
        capture: true,
      });
    });

    this.schedule();
  }

  /** Fija un estilo explicito hasta el `popOverride` correspondiente. */
  pushOverride(style: StatusBarStyleValue): void {
    this.overrides.push(style);
    this.apply(style);
  }

  /** Quita el ultimo override y vuelve a lo que corresponda. */
  popOverride(): void {
    this.overrides.pop();
    this.schedule();
  }

  /**
   * Agenda una medicion para el proximo frame. El scroll dispara decenas de eventos por
   * segundo y medir estilos computados fuerza reflow, asi que se colapsa a uno por frame.
   */
  private schedule(): void {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => {
      this.scheduled = false;
      this.sync();
    });
  }

  private sync(): void {
    const activeOverride = this.overrides[this.overrides.length - 1];
    this.apply(activeOverride ?? this.detectStyle());
  }

  private apply(style: StatusBarStyleValue): void {
    if (style === this.lastSent) return;
    this.lastSent = style;
    this.wvCommunicationService.setStatusBarStyle(style);
  }

  /**
   * Deriva el estilo del color que efectivamente se ve en la franja de la status bar.
   *
   * Se toma un punto en el medio de esa franja y se recorren los elementos que hay bajo
   * el, de arriba hacia abajo, hasta encontrar el primero con un fondo lo bastante opaco
   * como para definir el color que ve el usuario. Los fondos translucidos por encima de
   * ese umbral (el header con blur usa alpha 0.7) ya mandan sobre lo que tapan.
   */
  private detectStyle(): StatusBarStyleValue {
    const safeTop = this.getSafeTop();
    // Sin franja no hay nada que resolver: es el navegador, no la app.
    if (safeTop <= 0) return 'dark';

    const x = Math.round(this.document.defaultView!.innerWidth / 2);
    const y = Math.max(1, Math.round(safeTop / 2));

    for (const element of this.document.elementsFromPoint(x, y)) {
      const color = this.parseColor(getComputedStyle(element).backgroundColor);
      if (!color || color.a < 0.5) continue;
      // Fondo claro → iconos oscuros; fondo oscuro → iconos claros.
      return this.luminance(color) > 0.5 ? 'dark' : 'light';
    }

    // Sin ningun fondo opaco (todo transparente): el portal es claro por defecto.
    return 'dark';
  }

  private getSafeTop(): number {
    const raw = getComputedStyle(this.document.documentElement).getPropertyValue('--bp-safe-top');
    return parseFloat(raw) || 0;
  }

  /** Parsea el `rgb()` / `rgba()` que devuelve siempre `getComputedStyle`. */
  private parseColor(value: string): { r: number; g: number; b: number; a: number } | null {
    const match = value.match(/rgba?\(([^)]+)\)/);
    if (!match) return null;
    const parts = match[1].split(',').map(part => parseFloat(part));
    if (parts.length < 3 || parts.some(isNaN)) return null;
    return { r: parts[0], g: parts[1], b: parts[2], a: parts.length > 3 ? parts[3] : 1 };
  }

  /** Luminancia relativa (WCAG 2.1): 0 = negro, 1 = blanco. */
  private luminance({ r, g, b }: { r: number; g: number; b: number }): number {
    const channel = (value: number) => {
      const v = value / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  }
}
